<template>
  <v-form>
    <validation-observer
      ref="observer"
      v-slot="{ invalid, reset, handleSubmit }"
    >
      <v-card>
        <v-card-title>
          {{
            model.id ? "Изменить документ продажи" : "Создать документ продажи"
          }}
          <v-spacer />
          <span
            v-if="model.counteragent && !loading"
            :class="
              counteragentWithTotals.calculation_totals > 0
                ? 'success--text text--darken-3'
                : 'error--text text--darken-2'
            "
          >
            <template v-if="counteragentWithTotalsLoading">
              <v-progress-circular
                width="2"
                indeterminate
              ></v-progress-circular>
            </template>
            <span v-else>
              <span v-if="counteragentWithTotals.calculation_totals < 0"
                >Мы должны:</span
              >
              <span v-else>Нам должны:</span>
              {{ counteragentWithTotals.calculation_totals | money }}
            </span>
          </span>
        </v-card-title>
        <v-divider></v-divider>
        <div v-if="loading || fetching">
          <v-progress-linear indeterminate color="primary"></v-progress-linear>
        </div>
        <v-card-text v-else>
          <v-row>
            <v-col cols="3">
              <validation-provider
                v-slot="{ errors }"
                name="reg_date"
                rules="required|max:256"
              >
                <v-menu
                  ref="consignmentDate"
                  :disabled="regDateIsBlocked"
                  v-model="consignmentDateMenu"
                  :close-on-content-click="false"
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="model.reg_date | date"
                      label="Дата"
                      :disabled="regDateIsBlocked"
                      prepend-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                      readonly
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="model.reg_date"
                    @input="consignmentDateMenu = false"
                    :allowed-dates="allowedRegDates"
                    :disabled="regDateIsBlocked"
                  ></v-date-picker>
                </v-menu>
              </validation-provider>
            </v-col>
            <v-col cols="6">
              <validation-provider
                v-slot="{ errors }"
                name="counteragent"
                rules="required|max:256"
              >
                <v-autocomplete
                  v-model="model.counteragent"
                  :items="counteragents"
                  item-value="id"
                  item-text="full_name"
                  label="Контрагент"
                  :loading="counteragentsLoading"
                  required
                  :error-messages="errors"
                  @change="getCalculationTotals"
                ></v-autocomplete>
              </validation-provider>
            </v-col>
            <v-col cols="3">
              <v-select
                v-model="model.currency"
                class="inner-elevation-0"
                :items="currencies"
                label="Валюта"
                item-value="id"
                item-text="title"
                hide-details
                @change="updateItemPrices"
              ></v-select>
            </v-col>

            <v-col cols="6">
              <v-combobox
                v-model="transportValue"
                :items="transportOptions"
                :loading="optionsLoading"
                item-text="label"
                item-value="id"
                return-object
                clearable
                label="Транспорт"
                prepend-icon="mdi-truck-outline"
                hint="Выберите из списка или введите вручную"
                persistent-hint
                @change="onTransportChange"
                @update:search-input="onTransportTyped"
              ></v-combobox>
            </v-col>

            <v-col cols="6">
              <v-combobox
                v-model="driverValue"
                :items="driverOptions"
                :loading="optionsLoading"
                item-text="name"
                item-value="id"
                return-object
                clearable
                label="Водитель"
                prepend-icon="mdi-account-tie"
                hint="Выберите сотрудника или введите вручную"
                persistent-hint
                @change="onDriverChange"
                @update:search-input="onDriverTyped"
              ></v-combobox>
            </v-col>

            <v-col cols="9">
              <p>
                Элементы (всего позиций: {{ (consignmentItems || []).length }})
              </p>

              <template v-for="(item, index) in consignmentItems">
                <v-row>
                  <v-col cols="6">
                    <validation-provider
                      v-slot="{ errors }"
                      :name="`product_type.${index}`"
                      rules="required|double"
                    >
                      <v-autocomplete
                        v-model="item.product_type"
                        :items="products"
                        :loading="productsLoading"
                        @change="setItemDefaults(index)"
                        item-value="id"
                        item-text="name"
                        required
                        :error-messages="errors"
                        label="Продукт"
                        dense
                      ></v-autocomplete>
                    </validation-provider>
                  </v-col>
                  <v-col cols="1">
                    <validation-provider
                      v-slot="{ errors }"
                      :name="`amount.${index}`"
                      rules="required|integer"
                    >
                      <v-text-field
                        v-model.number="item.amount"
                        @keyup="setItemPrice(index)"
                        required
                        :error-messages="errors"
                        label="Кол-во"
                        dense
                      ></v-text-field>
                    </validation-provider>
                  </v-col>

                  <v-col cols="2">
                    <validation-provider
                      v-slot="{ errors }"
                      :name="`price.${index + 1}`"
                      rules="required|double"
                    >
                      <v-text-field
                        v-model.number="item.price"
                        :suffix="priceSuffix"
                        @keyup="setItemPrice(index)"
                        required
                        :error-messages="errors"
                        label="Цена"
                        dense
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      v-model="item.total"
                      readonly
                      :suffix="priceSuffix"
                      append-outer-icon="mdi-close"
                      label="Всего"
                      dense
                      @click:append-outer="removeItem(index)"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </template>

              <v-btn
                @click="createItem"
                color="primary"
                small
                :loading="productsLoading"
                :disabled="productsLoading"
              >
                <v-icon small left>mdi-plus</v-icon> Добавить продукт
              </v-btn>
            </v-col>
            <v-col cols="3" class="text-right">
              <h4>Промежуточный итог:</h4>
              <span class="display-1">{{
                rawTotal | money(model.currency)
              }}</span>
              <h4 class="mt-3">Итого:</h4>
              <span class="display-1">{{ total | money(model.currency) }}</span>
              <div v-if="total - rawTotal" class="success--text">
                {{
                  parseFloat((total - rawTotal).toFixed(this.pricePrecision))
                    | money(model.currency)
                }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-space-between pb-4 mt-2 px-4">
          <v-btn
            @click="handleSubmit(save)"
            :loading="!!saving"
            :disabled="!!saving || invalid"
            rounded
            color="success"
          >
            <v-icon left>mdi-content-save</v-icon>
            {{ model.id ? "Сохранить" : "Создать" }}
          </v-btn>
          <v-btn
            @click="cancel(reset)"
            :disabled="!!saving"
            :loading="!!saving"
            rounded
            text
            color="error"
          >
            <v-icon left>mdi-cancel</v-icon>
            Отмена
          </v-btn>
        </v-card-actions>
      </v-card>
    </validation-observer>
  </v-form>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from "vuex";
import moment from "moment";
import { filters } from "@/filters";

export default {
  name: "consignment-form",

  data() {
    return {
      consignmentDateMenu: false,
      loading: true,

      transports: [],
      staff: [],
      optionsLoading: false,
      // combobox values: an option object (company transport / staff) or a typed-in string
      transportValue: null,
      driverValue: null,
    };
  },

  computed: {
    ...mapState("Consignment", [
      "saving",
      "fetching",
      "model",
      "consignmentItems",
    ]),

    ...mapState("Counteragent", {
      counteragents: (state) => state.filterData?.results || [],
      counteragentsLoading: (state) => !!state.fetching,
      counteragentWithTotals: (state) => state.modelWithTotals || {},
      counteragentWithTotalsLoading: (state) => !!state.fetchingDetails,
    }),

    ...mapGetters("Counteragent", ["getFilterCounteragent"]),

    ...mapState("Product", {
      products: (state) => state.data?.results || [],
      productsLoading: (state) => !!state.fetching,
    }),
    ...mapGetters("Product", ["getProduct"]),

    ...mapState("CurrencyRate", {
      currencies: (state) => state.Currencies,
    }),
    ...mapGetters("CurrencyRate", ["getCurrency"]),

    total() {
      return (this.consignmentItems || [])
        .reduce((total, ti) => total + (ti.amount || 0) * (ti.price || 0), 0)
        .toFixed(this.pricePrecision);
    },

    rawTotal() {
      return (this.consignmentItems || [])
        .reduce((total, ti) => total + (ti.amount || 0) * (ti.price || 0), 0)
        .toFixed(this.pricePrecision);
    },

    regDateIsBlocked() {
      return (
        (this.model.id &&
          moment(this.model.reg_date).isBefore(moment().startOf("day"))) ||
        !this.userService.can("can_consignment_all_date")
      );
    },

    priceField() {
      return this.model.currency === "usd" ? "sell_price_usd" : "sell_price";
    },

    pricePrecision() {
      return this.getCurrency(this.model.currency).precision;
    },

    priceSuffix() {
      return this.getCurrency(this.model.currency).format;
    },

    transportOptions() {
      return this.transports.map((t) => ({
        id: t.id,
        label: [t.number, t.model].filter(Boolean).join(" - "),
        driver_staff: t.driver_staff,
        driver_name: t.driver_name,
      }));
    },

    driverOptions() {
      return this.staff.map((s) => ({
        id: s.id,
        name: filters.full_name(s).replace(/s+/g, " ").trim(),
      }));
    },
  },

  methods: {
    ...mapMutations("Consignment", [
      "createItem",
      "setModel",
      "setItem",
      "updateModel",
    ]),

    ...mapActions("Consignment", ["removeItem"]),

    ...mapActions("Counteragent", ["showWithTotals"]),
    getCalculationTotals(counteragentId) {
      return this.showWithTotals(
        this.counteragents.find((item) => item.id === counteragentId)
      );
    },

    allowedRegDates(val) {
      return moment().isAfter(moment(val));
    },

    setItemDefaults(index) {
      let item = this.consignmentItems[index];
      item.id = null;
      item.price = parseFloat(
        (
          (this.getProduct(item.product_type) || {})[this.priceField] || 0
        ).toFixed(this.pricePrecision)
      );
      item.total = parseFloat(
        (item.price * item.amount || 0).toFixed(this.pricePrecision)
      );
      this.setItem({ index, item });
    },

    setItemPrice(index) {
      let item = this.consignmentItems[index];
      item.total = parseFloat(
        (item.amount * item.price).toFixed(this.pricePrecision)
      );

      this.setItem({ index, item });
    },

    updateItemPrices() {
      this.$nextTick(() =>
        this.consignmentItems.forEach((item, i) => {
          item.price = parseFloat(
            (this.getProduct(item.product_type)[this.priceField] || 0).toFixed(
              this.pricePrecision
            )
          );
          this.setItemPrice(i);
        })
      );
    },

    staffOption(staffId, name) {
      if (!staffId) return null;
      return (
        this.driverOptions.find((o) => o.id === staffId) ||
        // e.g. a driver who is no longer active
        { id: staffId, name: name || `#${staffId}` }
      );
    },

    // Picked a company transport -> its driver is preselected; typed text -> transport outside the system
    onTransportChange(value) {
      if (value && typeof value === "object") {
        this.model.transport = value.id;
        this.model.non_company_transport = value.label;
        if (value.driver_staff) {
          this.driverValue = this.staffOption(value.driver_staff, value.driver_name);
          this.onDriverChange(this.driverValue);
        }
      } else {
        this.model.transport = null;
        this.model.non_company_transport = (value || "").trim();
      }
    },

    // Picked a staff member -> stored as the driver; typed text -> driver outside the staff
    onDriverChange(value) {
      if (value && typeof value === "object") {
        this.model.driver_staff = value.id;
        this.model.non_staff_driver = value.name;
      } else {
        this.model.driver_staff = null;
        this.model.non_staff_driver = (value || "").trim();
      }
    },

    // A combobox commits typed text only on blur/Enter, which happens after a click on "Сохранить" has
    // already started saving. Track the text as it is typed so the document gets what is in the field.
    onTransportTyped(text) {
      if (text === null || text === undefined) return;
      const selected = this.transportValue;
      if (selected && typeof selected === "object" && selected.label === text) return;
      this.model.transport = null;
      this.model.non_company_transport = text.trim();
    },

    onDriverTyped(text) {
      if (text === null || text === undefined) return;
      const selected = this.driverValue;
      if (selected && typeof selected === "object" && selected.name === text) return;
      this.model.driver_staff = null;
      this.model.non_staff_driver = text.trim();
    },

    loadTransportAndDriverOptions() {
      this.optionsLoading = true;
      return Promise.all([
        this.$store.dispatch("Transport/fetchOptions"),
        this.$store.dispatch("Staff/fetchOptions"),
      ])
        .then(([transports, staff]) => {
          this.transports = transports;
          this.staff = staff;
          this.syncTransportAndDriverFromModel();
        })
        .finally(() => (this.optionsLoading = false));
    },

    // Existing document: show what was saved (a system record or the typed-in text)
    syncTransportAndDriverFromModel() {
      this.transportValue =
        this.transportOptions.find((o) => o.id === this.model.transport) ||
        this.model.non_company_transport ||
        null;
      this.driverValue =
        this.staffOption(this.model.driver_staff, this.model.non_staff_driver) ||
        this.model.non_staff_driver ||
        null;
    },

    cancel(reset) {
      reset();
      this.$emit("consignment:form:cancel");
    },

    save() {
      this.$store.dispatch("Consignment/save").then(() => {
        this.$emit("consignment:form:saved");
      });
    },
  },

  watch: {
    // the document may be (re)loaded or reset after the form is mounted
    model() {
      this.syncTransportAndDriverFromModel();
    },
  },

  mounted() {
    this.loadTransportAndDriverOptions();
    Promise.all([this.$store.dispatch("Product/fetch")]).then(() => {
      this.loading = false;
      if (this.model.counteragent) {
        this.getCalculationTotals(
          this.counteragents.find((item) => item.id === this.model.counteragent)
        );
      }
    });
  },
};
</script>
